import { useEffect, useImperativeHandle, useRef, useState } from 'react';

import clsx from 'clsx';

import { WithRef } from '../../../../types';
import {
  DEFAULT_MAX_RESIZE_WIDTH,
  DEFAULT_MIN_RESIZE_WIDTH,
} from '../../constants';
import { useTableContext } from '../../hooks';
import { ResizerLineApi } from '../../types';
import { checkResizableColumnWidthLimits } from '../../utils';

export const ResizerLine = ({ ref }: WithRef<ResizerLineApi>) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const mouseMoveHandlerRef = useRef<((event: MouseEvent) => void) | null>(
    null
  );
  const mouseUpHandlerRef = useRef<(() => void) | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const {
    tableProps: { ResizerIcon },
  } = useTableContext()!;

  useEffect(() => {
    return () => {
      const moveHandler = mouseMoveHandlerRef.current;
      const upHandler = mouseUpHandlerRef.current;
      if (moveHandler) {
        document.body.removeEventListener('mousemove', moveHandler);
      }
      if (upHandler) {
        document.body.removeEventListener('mouseup', upHandler);
      }
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      startResizing: ({ column, startingPoint, onResizingEnded }) => {
        const overlay = rootRef.current?.parentElement;
        const toRelativeX = (clientX: number) => {
          return clientX - (overlay?.getBoundingClientRect().left || 0);
        };
        setIsResizing(true);
        let moved = 0;
        if (rootRef.current) {
          rootRef.current.style.left = `${toRelativeX(startingPoint)}px`;
        }
        const previousUserSelect = document.body.style.userSelect;
        document.body.style.userSelect = 'none';
        const onMouseMove = (event: MouseEvent) => {
          moved = event.clientX - startingPoint;
          const newWidth = column.computedWidth + moved;
          if (
            newWidth >= (column.minResizeWidth || DEFAULT_MIN_RESIZE_WIDTH) &&
            newWidth <= (column.maxResizeWidth || DEFAULT_MAX_RESIZE_WIDTH) &&
            rootRef.current
          ) {
            rootRef.current.style.transform = `translateX(${moved}px)`;
          }
        };
        const onMouseUp = () => {
          document.body.style.userSelect = previousUserSelect;
          document.body.removeEventListener('mouseup', onMouseUp);
          document.body.removeEventListener('mousemove', onMouseMove);
          mouseUpHandlerRef.current = null;
          mouseMoveHandlerRef.current = null;
          setIsResizing(false);

          const newWidth = checkResizableColumnWidthLimits({
            width: column.computedWidth + moved,
            minResizeWidth: column.minResizeWidth,
            maxResizeWidth: column.maxResizeWidth,
          });

          onResizingEnded(newWidth);
          if (rootRef.current) {
            rootRef.current.style.transform = 'unset';
          }
        };
        document.body.addEventListener('mouseup', onMouseUp);
        document.body.addEventListener('mousemove', onMouseMove);
        mouseUpHandlerRef.current = onMouseUp;
        mouseMoveHandlerRef.current = onMouseMove;
      },
    }),
    [rootRef]
  );

  return (
    <div
      ref={rootRef}
      className={clsx('DS-Table-ResizerLine', {
        'DS-Table-resizing': isResizing,
      })}
    >
      <div className="DS-Table-ResizerIconWrapper">
        <ResizerIcon className="DS-Table-ResizerIcon" />
      </div>
    </div>
  );
};

ResizerLine.displayName = 'ResizerLine';
